import moment from "moment";
import { SourceOption } from "@/features/annotation/lib/types";
import { Stage, StageStatus, StageDetailStatus } from "@/shared/api/lod-api";
import {
  TABLE_COLUMNS,
  ORDER_SOURCE,
  STAGE_STATUS,
  BADGE_COLOR,
  STAGE_STATUS_TEXT,
  type BadgeColor,
} from "./types";

export const visibleColumnNames: Record<string, string> = {
  [TABLE_COLUMNS.SELECT]: "Select",
  [TABLE_COLUMNS.ID]: "Order ID",
  [TABLE_COLUMNS.TIME]: "Created at",
  [TABLE_COLUMNS.ORDER_SOURCE]: "Source",
  [TABLE_COLUMNS.ANNOTATOR_ID]: "Current\nAssignee",
  [TABLE_COLUMNS.STATUS]: "Status",
  [TABLE_COLUMNS.ANOMALY_REPORT]: "Anomaly\nReport",
};

const smartTextBreak = (text: string, maxSingleLineLength = 10): string => {
  if (text.length <= maxSingleLineLength) {
    return text;
  }

  const words = text.split(" ");
  if (words.length === 1) {
    return text;
  }

  const midPoint = Math.ceil(words.length / 2);
  const firstPart = words.slice(0, midPoint).join(" ");
  const secondPart = words.slice(midPoint).join(" ");

  return `${firstPart}\n${secondPart}`;
};

export const generateDynamicColumnNames = (
  stages: Stage[]
): Record<string, string> => {
  const dynamicColumns: Record<string, string> = {};

  stages.forEach((stage) => {
    const formattedName = formatStageName(stage.code);

    const stageColumnName = smartTextBreak(formattedName, 12);

    const rejectionText = `${formattedName} Rejection`;
    const rejectionColumnName = smartTextBreak(rejectionText, 8);

    dynamicColumns[`stage_${stage.code}`] = stageColumnName;
    dynamicColumns[`rejection_${stage.code}`] = rejectionColumnName;
  });

  return dynamicColumns;
};

export const formatStageName = (stageName: string): string => {
  return stageName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getInitialSorting = (searchParams: URLSearchParams) => {
  const ordering = searchParams.get("ordering");

  if (ordering) {
    return ordering.split(",").map((key) => ({
      id: key.replace("-", ""),
      desc: key.startsWith("-") ? true : false,
    }));
  }

  return [{ id: "time", desc: true }];
};

export const sourceOptions: SourceOption[] = [
  { key: ORDER_SOURCE.SKU_IN_BOX, value: ORDER_SOURCE.SKU_IN_BOX },
  { key: ORDER_SOURCE.MOBILE_APP_HITL, value: ORDER_SOURCE.MOBILE_APP_HITL },
  { key: ORDER_SOURCE.COFFEE_MACHINE, value: ORDER_SOURCE.COFFEE_MACHINE },
  { key: ORDER_SOURCE.TEMPORARY_ORDER, value: ORDER_SOURCE.TEMPORARY_ORDER },
  {
    key: ORDER_SOURCE.TRACKER_TEST_SETUP,
    value: ORDER_SOURCE.TRACKER_TEST_SETUP,
  },
  {
    key: ORDER_SOURCE.ANONYMOUS_JOURNEY_RECORDINGS,
    value: ORDER_SOURCE.ANONYMOUS_JOURNEY_RECORDINGS,
  },
  {
    key: ORDER_SOURCE.JOURNEY_RECORDINGS,
    value: ORDER_SOURCE.JOURNEY_RECORDINGS,
  },
];

export const formatDateTime = (timeValue: string): string => {
  if (!timeValue) return "";
  return moment.utc(timeValue).format("HH:mm MMMM D, YYYY");
};

export const getStageStatusColor = (status: StageStatus): BadgeColor => {
  switch (status) {
    case STAGE_STATUS.DONE:
      return BADGE_COLOR.GREEN;
    case STAGE_STATUS.REJECTED:
      return BADGE_COLOR.RED;
    case STAGE_STATUS.NOT_STARTED:
    default:
      return BADGE_COLOR.GRAY;
  }
};

export const formatStageStatusText = (status: StageStatus): string => {
  return (
    STAGE_STATUS_TEXT[status] || STAGE_STATUS_TEXT[STAGE_STATUS.NOT_STARTED]
  );
};

export const createStatusTextMapper = (apiStatuses?: string[]) => {
  const statusTextMap: Record<string, string> = {
    [STAGE_STATUS.NOT_STARTED]: STAGE_STATUS_TEXT[STAGE_STATUS.NOT_STARTED],
    [STAGE_STATUS.DONE]: STAGE_STATUS_TEXT[STAGE_STATUS.DONE],
    [STAGE_STATUS.REJECTED]: STAGE_STATUS_TEXT[STAGE_STATUS.REJECTED],
  };

  if (apiStatuses) {
    apiStatuses.forEach((status) => {
      if (!statusTextMap[status]) {
        statusTextMap[status] = status
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }
    });
  }

  return statusTextMap;
};

export const createStageStatusOptions = (apiStatuses?: string[]) => {
  const statusTextMap = createStatusTextMapper(apiStatuses);
  const statusesToUse = apiStatuses || [
    STAGE_STATUS.NOT_STARTED,
    STAGE_STATUS.DONE,
    STAGE_STATUS.REJECTED,
  ];

  return statusesToUse.map((status) => ({
    key: status,
    value: statusTextMap[status] || status,
  }));
};

export const createCompleteStageStatuses = (
  allStages: Stage[],
  currentStatuses: StageDetailStatus[],
  originalStatuses: StageDetailStatus[]
) => {
  const currentChangesMap = new Map(
    currentStatuses.map((status) => [status.stage, status])
  );

  const originalStatusesMap = new Map(
    originalStatuses.map((status) => [status.stage, status])
  );

  return allStages.map((stage) => {
    const currentStatus = currentChangesMap.get(stage.code);
    const originalStatus = originalStatusesMap.get(stage.code);

    const finalStatus = currentStatus ||
      originalStatus || {
        stage: stage.code,
        status: STAGE_STATUS.NOT_STARTED,
        rejection_reason: null,
      };

    return {
      stage: finalStatus.stage,
      status: finalStatus.status,
      rejection_reason: finalStatus.rejection_reason || null,
    };
  });
};
